import React, {memo, useMemo, useContext} from 'react'
import {H5, Row, Col} from '@modifi/ui'
import {
  CountrySelectPureWithInputContainer,
  DateInputWithInputContainer,
  InputPureWithInputContainer,
  useInputIsVisible,
  useRenderPersistedObject,
  shouldRenderComponentWhenLegalRepAndNotSignatory,
  AddressForm,
  PersonDocumentUploadWithInputContainer,
  InputTooltipInfo,
} from '@modifi/plugin-onboarding'
import {FormDataContext} from '@modifi/plugin-forms'

import {getInputName} from './utils'
import {
  ADDRESS_DATE_OF_BIRTH,
  ADDRESS_EMAIL,
  ADDRESS_NATIONALITY,
  ADDRESS_ADDITIONAL_NATIONALITIES,
  ADDRESS_COUNTRY_OF_BIRTH,
  PASSPORT_DOCUMENT,
} from './constants'
import {
  FormInputComponentCol,
  FormUploadComponentCol,
  ExistingFilesList,
} from '../../plugins/onboarding'
import {FormRow} from '../../ui/form'
import {TranslateFn} from '../../types/common'

type PersonFormProps = {
  formType: string
  t: TranslateFn
}

const PersonForm: React.FC<PersonFormProps> = ({t, formType}) => {
  const nationalityInputName = getInputName(formType, ADDRESS_NATIONALITY)
  const dateOfBirthInputName = getInputName(formType, ADDRESS_DATE_OF_BIRTH)
  const emailInputName = getInputName(formType, ADDRESS_EMAIL)
  const additionalNationalitiesInputName = getInputName(formType, ADDRESS_ADDITIONAL_NATIONALITIES)
  const countryOfBirthInputName = getInputName(formType, ADDRESS_COUNTRY_OF_BIRTH)
  const passportDocInputName = getInputName(formType, PASSPORT_DOCUMENT)

  const nationalityInputIsVisible = useInputIsVisible(nationalityInputName)
  const dateOfBirthInputIsVisible = useInputIsVisible(dateOfBirthInputName)
  const additionalNationalitiesInputIsVisible = useInputIsVisible(additionalNationalitiesInputName)
  const countryOfBirthInputIsVisible = useInputIsVisible(countryOfBirthInputName)
  const emailInputIsVisible = useInputIsVisible(emailInputName)
  const isPassportDocVisible = useInputIsVisible(passportDocInputName)
  const filterParams = useRenderPersistedObject({formType})

  const {data} = useContext(FormDataContext)

  const additionalNationalityValues = (
    additionalNationalitiesInputName ? data[additionalNationalitiesInputName] || [] : []
  ) as string[]
  const nationalityValue = nationalityInputName && data[nationalityInputName]

  return (
    <>
      {useMemo(
        () => (
          <Row mb={16}>
            <Col>
              <H5>{t('person-form.identification.headline')}</H5>
            </Col>
          </Row>
        ),
        [t]
      )}

      <Row>
        <Col col={12}>
          <AddressForm
            formType={formType}
            components={{
              companyName: null,
              countryCode: null,
              streetName: null,
              streetNumber: null,
              postCode: null,
              city: null,
              zone: null,
            }}
            // eslint-disable-next-line react/jsx-curly-brace-presence
            format={`{salutation}{firstName}_{middleName}{lastName}`}
          />
        </Col>
      </Row>

      {((dateOfBirthInputName && dateOfBirthInputIsVisible) ||
        (emailInputName && emailInputIsVisible)) && (
        <FormRow>
          {dateOfBirthInputName && dateOfBirthInputIsVisible && (
            <FormInputComponentCol
              filterFunc={shouldRenderComponentWhenLegalRepAndNotSignatory}
              filterParams={filterParams}
              t={t}
              inputName={dateOfBirthInputName}
              inputComponent={DateInputWithInputContainer}
            />
          )}
          {emailInputName && emailInputIsVisible && (
            <FormInputComponentCol
              filterFunc={shouldRenderComponentWhenLegalRepAndNotSignatory}
              filterParams={filterParams}
              t={t}
              inputName={emailInputName}
              inputComponent={InputPureWithInputContainer}
            />
          )}
        </FormRow>
      )}

      <FormRow>
        {countryOfBirthInputName && countryOfBirthInputIsVisible && (
          <FormInputComponentCol
            filterFunc={shouldRenderComponentWhenLegalRepAndNotSignatory}
            filterParams={filterParams}
            t={t}
            inputName={countryOfBirthInputName}
            inputComponent={CountrySelectPureWithInputContainer}
            inputProps={{includeSanctionedCountries: true}}
          />
        )}
        {nationalityInputName && nationalityInputIsVisible && (
          <FormInputComponentCol
            filterFunc={shouldRenderComponentWhenLegalRepAndNotSignatory}
            filterParams={filterParams}
            t={t}
            inputName={nationalityInputName}
            inputComponent={CountrySelectPureWithInputContainer}
            inputProps={{
              includeSanctionedCountries: true,
              ...(additionalNationalityValues?.length > 0
                ? {countryCodesToExclude: additionalNationalityValues}
                : {}),
            }}
          />
        )}
      </FormRow>

      {passportDocInputName && isPassportDocVisible && (
        <FormRow>
          <FormUploadComponentCol
            t={t}
            inputName={passportDocInputName}
            uploadFormComponent={PersonDocumentUploadWithInputContainer}
            colProps={{md: 12}}
            inputProps={{
              labelAddition: <InputTooltipInfo inputName={passportDocInputName} t={t} />,
              components: {
                existingFilesList: ExistingFilesList,
              },
            }}
          />
        </FormRow>
      )}

      {additionalNationalitiesInputName && additionalNationalitiesInputIsVisible && (
        <FormRow>
          <FormInputComponentCol
            filterFunc={shouldRenderComponentWhenLegalRepAndNotSignatory}
            filterParams={filterParams}
            t={t}
            inputName={additionalNationalitiesInputName}
            inputComponent={CountrySelectPureWithInputContainer}
            inputProps={{
              includeSanctionedCountries: true,
              isMulti: true,
              ...(nationalityValue ? {countryCodesToExclude: [nationalityValue]} : {}),
            }}
            colProps={{col: 12, md: 12, lg: 12}}
          />
        </FormRow>
      )}
    </>
  )
}

export default memo(PersonForm)
